import React, { useState, useEffect } from 'react';
import { COLORS } from '../config/constants';
import { stockService, projectService, mailService } from '../services/api.service';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import SendMailModal from '../components/SendMailModal';

const StockList = ({ user }) => {
  const [stocks, setStocks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('in'); // 'in' veya 'out'
  const [filters, setFilters] = useState({
    material_name: '',
    date_from: '',
    date_to: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showMailModal, setShowMailModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [stockData, projectData] = await Promise.all([
        stockService.getAll(),
        projectService.getAll()
      ]);
      setStocks(stockData);
      setProjects(projectData);
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (stock) => {
    setEditingStock({
      ...stock,
      material_name: stock.material_name || '',
      material_code: stock.material_code || '',
      quantity: stock.quantity || '',
      unit_weight: stock.unit_weight || '',
      total_weight: stock.total_weight || '',
      location: stock.location || '',
      supplier: stock.supplier || ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await stockService.update(editingStock.id, editingStock);
      setMessage({ type: 'success', text: 'Stok kaydı güncellendi!' });
      setShowEditModal(false);
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Güncelleme başarısız!' });
    }
  };

  const handleDelete = (id) => {
    setItemToDelete(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await stockService.delete(itemToDelete);
      setMessage({ type: 'success', text: 'Kayıt silindi!' });
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Silme işlemi başarısız!' });
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      setMessage({ type: 'success', text: 'Excel dosyası indiriliyor...' });
      await stockService.exportExcel(filters);
      setMessage({ type: 'success', text: 'Excel dosyası başarıyla indirildi!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Excel export başarısız!' });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      setMessage({ type: 'success', text: 'PDF dosyası indiriliyor...' });
      await stockService.exportPDF(filters);
      setMessage({ type: 'success', text: 'PDF dosyası başarıyla indirildi!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'PDF export başarısız!' });
    } finally {
      setExporting(false);
    }
  };


  const handleSendMail = async (recipients, attachExcel, attachPdf) => {
    try {
      await mailService.sendStockMail(recipients, attachExcel, attachPdf, filters);
      setMessage({ type: 'success', text: `Mail başarıyla ${recipients.length} alıcıya gönderildi!` });
    } catch (error) {
      console.error('Mail gönderme hatası:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Mail gönderilemedi!' });
    }
  };


  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setMessage({ type: 'error', text: 'Lütfen Excel dosyası (.xlsx veya .xls) seçin!' });
      return;
    }

    try {
      setImporting(true);
      setMessage({ type: 'success', text: 'Excel dosyası yükleniyor...' });
      
      const result = await stockService.importExcel(file);
      
      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: result.message || `Başarılı! ${result.imported} kayıt eklendi.`
        });
      } else {
        setMessage({ type: 'error', text: result.message || 'Import başarısız!' });
      }
      
      // Reload data
      loadData();
    } catch (error) {
      console.error('Import error:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Excel import başarısız!' });
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const clearFilters = () => {
    setFilters({
      material_name: '',
      date_from: '',
      date_to: ''
    });
  };

  const canEdit = user?.role === 'admin' || user?.role === 'operator';

  const filteredStocks = stocks.filter(stock => {
    // Tab filter - sadece aktif tab'a ait kayıtları göster
    const matchesTab = stock.entry_type === activeTab;
    
    const matchesSearch = 
      stock.material_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      stock.material_code?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesMaterialName = !filters.material_name || stock.material_name?.toLowerCase().includes(filters.material_name.toLowerCase());
    
    let matchesDateFrom = true;
    let matchesDateTo = true;
    
    if (filters.date_from && stock.created_at) {
      matchesDateFrom = new Date(stock.created_at) >= new Date(filters.date_from);
    }
    
    if (filters.date_to && stock.created_at) {
      matchesDateTo = new Date(stock.created_at) <= new Date(filters.date_to);
    }
    
    return matchesTab && matchesSearch && matchesMaterialName && matchesDateFrom && matchesDateTo;
  });

  // Get unique material names from FILTERED stocks (only visible records)
  const uniqueMaterialNames = [...new Set(filteredStocks.map(s => s.material_name).filter(Boolean))];

  return (
    <div style={{ 
      padding: '2rem',
      background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
      minHeight: '100vh'
    }}>
      {/* Modern Card Container */}
      <div style={{
        background: '#FFFFFF',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        border: '1px solid rgba(228, 10, 4, 0.1)'
      }}>
        {/* Modern Header with Gradient */}
        <div style={{
          background: 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)',
          padding: '2rem',
          color: '#FFFFFF'
        }}>
          <h1 style={{ fontSize: '2rem', fontWeight: '700', margin: 0, letterSpacing: '-0.5px' }}>
            📦 Stok Kayıtları
          </h1>
          <p style={{ fontSize: '1rem', opacity: 0.9, marginTop: '0.5rem' }}>
            Tüm stok giriş ve çıkış işlemlerini yönetin
          </p>
          
          {/* Stats Bar */}
          <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.25rem' }}>Toplam Giriş Miktar</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                {stocks.filter(s => s.entry_type === 'in').reduce((sum, s) => sum + (parseFloat(s.quantity) || 0), 0).toFixed(0)} kg
              </div>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.25rem' }}>Toplam Çıkış Miktar</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                {stocks.filter(s => s.entry_type === 'out').reduce((sum, s) => sum + (parseFloat(s.quantity) || 0), 0).toFixed(0)} kg
              </div>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.25rem' }}>Toplam Kayıt</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{stocks.length}</div>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.25rem' }}>Filtreli Kayıt</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{filteredStocks.length}</div>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.25rem' }}>Filtreli Ağırlık</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                {filteredStocks.reduce((sum, s) => sum + (parseFloat(s.quantity) || 0), 0).toFixed(0)} kg
              </div>
            </div>
          </div>
        </div>

        {/* Modern Tabs */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          padding: '1.5rem 2rem',
          borderBottom: '1px solid #E8E8E8',
          background: '#FAFAFA'
        }}>
          <button
            onClick={() => setActiveTab('in')}
            style={{
              padding: '0.875rem 2rem',
              background: activeTab === 'in' ? 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)' : 'transparent',
              color: activeTab === 'in' ? '#FFFFFF' : '#666666',
              border: 'none',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: activeTab === 'in' ? '0 4px 12px rgba(228, 10, 4, 0.3)' : 'none',
              transform: activeTab === 'in' ? 'translateY(-2px)' : 'none'
            }}
          >
            📦 Stok Giriş ({stocks.filter(s => s.entry_type === 'in').length})
          </button>
          <button
            onClick={() => setActiveTab('out')}
            style={{
              padding: '0.875rem 2rem',
              background: activeTab === 'out' ? 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)' : 'transparent',
              color: activeTab === 'out' ? '#FFFFFF' : '#666666',
              border: 'none',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: activeTab === 'out' ? '0 4px 12px rgba(228, 10, 4, 0.3)' : 'none',
              transform: activeTab === 'out' ? 'translateY(-2px)' : 'none'
            }}
          >
            📤 Stok Çıkış ({stocks.filter(s => s.entry_type === 'out').length})
          </button>
        </div>

        {/* Controls Section - Modern */}
        <div style={{
          padding: '1.5rem 2rem',
          background: '#FFFFFF',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
          borderBottom: '1px solid #E8E8E8'
        }}>
          <div style={{ flex: '1', maxWidth: '500px', position: 'relative' }}>
            <span style={{
              position: 'absolute',
              left: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '1.2rem',
              color: '#999'
            }}>🔍</span>
            <input
              type="text"
              placeholder="Malzeme adı veya kodu ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.875rem 1rem 0.875rem 3rem',
                border: '2px solid #E8E8E8',
                borderRadius: '10px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.border = '2px solid #E40A04';
                e.target.style.boxShadow = '0 4px 12px rgba(228, 10, 4, 0.15)';
              }}
              onBlur={(e) => {
                e.target.style.border = '2px solid #E8E8E8';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          
          {/* Modern Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              padding: '0.875rem 1.75rem',
              background: showFilters ? 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)' : '#FFFFFF',
              color: showFilters ? '#FFFFFF' : '#333333',
              border: showFilters ? 'none' : '2px solid #E40A04',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: showFilters 
                ? '0 4px 12px rgba(228, 10, 4, 0.3)'
                : '0 2px 8px rgba(0, 0, 0, 0.08)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            🔍 Filtreler
          </button>
          <button
            onClick={handleImportClick}
            disabled={importing}
            style={{
              padding: '0.875rem 1.75rem',
              background: '#3B82F6',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: importing ? 'not-allowed' : 'pointer',
              opacity: importing ? 0.5 : 1,
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            📥 Excel Yükle
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <button
            onClick={handleExportExcel}
            disabled={exporting || filteredStocks.length === 0}
            style={{
              padding: '0.875rem 1.75rem',
              background: '#10B981',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: exporting || filteredStocks.length === 0 ? 'not-allowed' : 'pointer',
              opacity: exporting || filteredStocks.length === 0 ? 0.5 : 1,
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            📊 Excel İndir
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exporting || filteredStocks.length === 0}
            style={{
              padding: '0.875rem 1.75rem',
              background: 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: exporting || filteredStocks.length === 0 ? 'not-allowed' : 'pointer',
              opacity: exporting || filteredStocks.length === 0 ? 0.5 : 1,
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(228, 10, 4, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            📄 PDF İndir
          </button>
          <button
            onClick={() => setShowMailModal(true)}
            style={{
              padding: '0.875rem 1.75rem',
              background: '#3B82F6',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            📧 Mail Gönder
          </button>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div style={{
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
          color: message.type === 'success' ? '#065f46' : '#991b1b'
        }}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div style={{ 
          background: COLORS.white, 
          borderRadius: '12px', 
          padding: '1.5rem',
          marginBottom: '2rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)' 
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.1rem' }}>Filtreleme Seçenekleri</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
                Malzeme Adı
              </label>
              <input
                type="text"
                list="material-names-list"
                value={filters.material_name}
                onChange={(e) => setFilters({...filters, material_name: e.target.value})}
                placeholder="Liste seç veya yaz..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `2px solid ${COLORS.background}`,
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  outline: 'none'
                }}
              />
              <datalist id="material-names-list">
                {uniqueMaterialNames.map(name => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
                Başlangıç Tarihi
              </label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `2px solid ${COLORS.background}`,
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  outline: 'none'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
                Bitiş Tarihi
              </label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `2px solid ${COLORS.background}`,
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  outline: 'none'
                }}
              />
            </div>
          </div>
          
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={clearFilters}
              style={{
                padding: '0.5rem 1rem',
                background: COLORS.background,
                color: COLORS.text,
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.9rem',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Filtreleri Temizle
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{ 
        background: COLORS.white, 
        borderRadius: '12px', 
        padding: '1.5rem',
        marginBottom: '2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)' 
      }}>
        <input
          type="text"
          placeholder="🔍 Malzeme adı veya kodu ile ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '0.875rem',
            border: `2px solid ${COLORS.background}`,
            borderRadius: '8px',
            fontSize: '1rem',
            outline: 'none'
          }}
        />
      </div>

      {/* Table */}
      <div style={{ 
        background: COLORS.white, 
        borderRadius: '12px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p style={{ color: COLORS.textLight }}>Yükleniyor...</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: COLORS.background }}>
                <th style={tableHeaderStyle}>Malzeme Adı</th>
                {activeTab === 'in' && <th style={tableHeaderStyle}>Sipariş No</th>}
                {activeTab === 'in' && <th style={tableHeaderStyle}>Rulo No</th>}
                {activeTab === 'in' && <th style={tableHeaderStyle}>İrsaliye No</th>}
                {activeTab === 'out' && <th style={tableHeaderStyle}>Heat No</th>}
                {activeTab === 'out' && <th style={tableHeaderStyle}>Rulo No</th>}
                <th style={tableHeaderStyle}>Miktar (kg)</th>
                {activeTab === 'in' && <th style={tableHeaderStyle}>Şirket Adı</th>}
                {activeTab === 'in' && <th style={tableHeaderStyle}>Fason Firma</th>}
                <th style={tableHeaderStyle}>Tarih</th>
                {canEdit && <th style={tableHeaderStyle}>İşlemler</th>}
              </tr>
            </thead>
            <tbody>
              {filteredStocks.map((stock, index) => {
                return (
                  <tr key={stock.id || index} style={{ borderBottom: `1px solid ${COLORS.background}` }}>
                    <td style={{...tableCellStyle, fontWeight: '600'}}>{stock.material_name}</td>
                    {activeTab === 'in' && <td style={tableCellStyle}>{stock.material_code || '-'}</td>}
                    {activeTab === 'in' && <td style={tableCellStyle}>{stock.rulo_numarasi || '-'}</td>}
                    {activeTab === 'in' && <td style={tableCellStyle}>{stock.irsaliye_numarasi || '-'}</td>}
                    {activeTab === 'out' && <td style={tableCellStyle}>{stock.heat_numarasi || '-'}</td>}
                    {activeTab === 'out' && <td style={tableCellStyle}>{stock.rulo_numarasi || '-'}</td>}
                    <td style={{...tableCellStyle, textAlign: 'center', fontWeight: '700', color: COLORS.primary}}>
                      {stock.quantity ? `${parseFloat(stock.quantity).toFixed(2)} kg` : '-'}
                    </td>
                    {activeTab === 'in' && <td style={tableCellStyle}>{stock.location || '-'}</td>}
                    {activeTab === 'in' && <td style={tableCellStyle}>{stock.supplier || '-'}</td>}
                    <td style={tableCellStyle}>
                      {stock.created_at ? new Date(stock.created_at).toLocaleDateString('tr-TR') : '-'}
                    </td>
                    {canEdit && (
                      <td style={tableCellStyle}>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleEdit(stock)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: '#3b82f6',
                              color: COLORS.white,
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer'
                            }}
                          >
                            Düzenle
                          </button>
                          <button
                            onClick={() => handleDelete(stock.id)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: COLORS.primary,
                              color: COLORS.white,
                              border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            cursor: 'pointer'
                          }}
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingStock && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: COLORS.white,
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginBottom: '1.5rem' }}>Stok Kaydını Düzenle</h2>
            <form onSubmit={handleUpdate}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <FormField label="Malzeme Adı" value={editingStock.material_name} 
                  onChange={(v) => setEditingStock({...editingStock, material_name: v})} required />
                <FormField label="Malzeme Kodu" value={editingStock.material_code}
                  onChange={(v) => setEditingStock({...editingStock, material_code: v})} />
                <FormField label="Miktar" type="number" step="0.01" value={editingStock.quantity}
                  onChange={(v) => setEditingStock({...editingStock, quantity: v})} required />
                <FormField label="Birim Ağırlık" type="number" step="0.01" value={editingStock.unit_weight}
                  onChange={(v) => setEditingStock({...editingStock, unit_weight: v})} />
                <FormField label="Toplam Ağırlık" type="number" step="0.01" value={editingStock.total_weight}
                  onChange={(v) => setEditingStock({...editingStock, total_weight: v})} />
                <FormField label="Konum" value={editingStock.location}
                  onChange={(v) => setEditingStock({...editingStock, location: v})} />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button type="submit" style={{
                  padding: '0.75rem 1.5rem',
                  background: COLORS.primary,
                  color: COLORS.white,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}>
                  Kaydet
                </button>
                <button type="button" onClick={() => setShowEditModal(false)} style={{
                  padding: '0.75rem 1.5rem',
                  background: COLORS.background,
                  color: COLORS.text,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}>
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      </div> {/* Close Modern Card */}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        itemType="stok kaydı"
      />

      {/* Send Mail Modal */}
      <SendMailModal
        isOpen={showMailModal}
        onClose={() => setShowMailModal(false)}
        onSend={handleSendMail}
        moduleName="Stok"
      />
    </div>
  );
};

const FormField = ({ label, value, onChange, type = 'text', step, required = false }) => (
  <div>
    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
      {label} {required && <span style={{ color: COLORS.primary }}>*</span>}
    </label>
    <input
      type={type}
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      style={{
        width: '100%',
        padding: '0.75rem',
        border: `1px solid ${COLORS.background}`,
        borderRadius: '8px',
        fontSize: '1rem'
      }}
    />
  </div>
);

const tableHeaderStyle = {
  padding: '1rem',
  textAlign: 'left',
  fontSize: '0.875rem',
  fontWeight: '600',
  textTransform: 'uppercase'
};

const tableCellStyle = {
  padding: '1rem',
  fontSize: '0.95rem'
};

export default StockList;
